import * as React from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { ApiClient } from '../../lib/ApiClient';
import { useAuthenticated } from '../auth/useAuthenticated'
import { useTheme, alpha } from '@mui/material/styles';
import { getUrl, useDomainNavigate, domain } from '../../domain';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import { grey } from '@mui/material/colors';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import PhonelinkLockIcon from '@mui/icons-material/PhonelinkLock';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import { NavLink } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import AppsIcon from '@mui/icons-material/Apps';
import Link from '@mui/material/Link';

const apiClient = new ApiClient();

export default function Home() {
    let navigate = useDomainNavigate();

    const userAuth = useAuthenticated(apiClient);
    const [isLoading] = React.useState<boolean>(false);
    const theme = useTheme();

    const appLinkStyles = {
        m: 1,
        p: 2,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: alpha(theme.palette.text.secondary, .1),
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.primary.light, .3)
    };

    if (!userAuth.isAuthenticating && !userAuth.isAuthenticated) {
        navigate("/account/login");
    }

    return (
        <>
            {isLoading &&
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <CircularProgress color="primary" size={40} aria-label="Loading..." title="Loading..." />
                </Box>
            }

            <Box
                sx={{
                    minWidth: 275,
                    marginTop: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                }}
            >
                <Box>
                    <Card variant="outlined" sx={{ m: 3 }}>
                        <CardHeader
                            avatar={
                                <WavingHandIcon sx={{ color: theme.palette.primary.main }} fontSize="large" />
                            }
                            title={"Welcome back " + (userAuth.result?.firstName ?? userAuth.result?.email)}
                            subheader="You're logged in. Continue to your previous app or manage your account here."
                            titleTypographyProps={{
                                sx: {
                                    fontWeight: '700',
                                    fontSize: '1.1em'
                                },
                            }}
                            sx={{ backgroundColor: alpha(theme.palette.primary.light, .2) }}
                        />
                        <CardContent>
                            <Box sx={{ mb: 1 }}>
                                {!userAuth.result?.firstName &&
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                        <Avatar sx={{ bgcolor: theme.palette.primary.main, color: grey[50] }} />
                                        <Typography>{userAuth.result?.email}</Typography>
                                    </Stack>
                                }
                                {userAuth.result?.firstName &&
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Avatar sx={{ bgcolor: theme.palette.primary.main, color: grey[50] }} />
                                        <Stack spacing={0} sx={{ lineHeight: 1 }}>
                                            <Typography sx={{ fontWeight: 'bold', lineHeight: 1.5 }}>
                                            {userAuth.result?.firstName + ' ' + userAuth.result?.lastName}
                                            </Typography>
                                            <Typography sx={{ fontSize: '.8em', color: theme.palette.text.secondary, lineHeight: 1 }}>
                                            {userAuth.result?.email}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                }
                            </Box>
                            <Box>
                                <Stack direction="row" spacing={1}>
                                    <Chip
                                        size="small"
                                        icon={<PhonelinkLockIcon />}
                                        label={userAuth.result?.twoFactorEnabled === true ? 'Multi-factor Authentication Enabled' : 'Multi-factor Authentication Disabled'}
                                        variant="outlined"
                                        color={userAuth.result?.twoFactorEnabled === true ? 'success' : 'warning'}
                                    />
                                    {!userAuth.result?.firstName &&
                                        <Chip
                                            size="small"
                                            icon={<SettingsIcon />}
                                            label="Profile Details Incomplete"
                                            variant="outlined"
                                            color="warning"
                                        />
                                    }
                                </Stack>
                            </Box>
                            <Divider sx={{ mt: 2, mb: 1 }}>

                            </Divider>
                            <Button
                                type="button"
                                variant="contained"
                                disableElevation
                                sx={{ mt: 1, mr: 1, backgroundColor: alpha(theme.palette.primary.light, .9) }}
                                component={NavLink}
                                to={getUrl("/account/manage/profile")}
                                startIcon={<SettingsIcon />}
                            >
                                Manage Profile
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                disableElevation
                                sx={{ mt: 1, mr: 1, backgroundColor: alpha(theme.palette.primary.light, .9) }}
                                component={NavLink}
                                to={getUrl("/account/manage/2fa")}
                                startIcon={<PhonelinkLockIcon />}
                            >
                                MFA Settings
                            </Button>
                        </CardContent>
                    </Card>
                    <Card variant="outlined" sx={{ m: 3 }}>
                        <CardHeader
                            title="Session Controls"
                            titleTypographyProps={{
                                sx: {
                                    fontWeight: '700',
                                    fontSize: '1.1em'
                                },
                            }}
                            avatar={
                                <LogoutIcon />
                            }
                            sx={{ backgroundColor: alpha(theme.palette.primary.light, .2) }}
                        />
                        <CardContent>
                            <Button
                                type="button"
                                variant="contained"
                                disableElevation
                                sx={{ m: 1, backgroundColor: alpha(theme.palette.primary.light, .9) }}
                                component={NavLink}
                                to={getUrl("/account/logout")}
                                startIcon={<LogoutIcon />}
                            >
                                Logout
                            </Button>
                            <div>
                                <Typography
                                    sx={{ ml: 1, fontSize: '0.9em', color: theme.palette.text.secondary }}
                                >
                                    Signing out will sign you out of all Utilismart applications simultaneously.
                                </Typography>
                            </div>
                        </CardContent>
                    </Card>
                </Box>
                {userAuth.result?.email.includes("@utilismartcorp.com") &&
                    <Box>
                        <Card variant="outlined" sx={{ m: 3 }}>
                            <CardHeader
                                title="Apps"
                                titleTypographyProps={{
                                    sx: {
                                        fontWeight: '700',
                                        fontSize: '1.1em'
                                    },
                                }}
                                avatar={
                                    <AppsIcon />
                                }
                                sx={{ backgroundColor: alpha(theme.palette.primary.light, .2) }}
                            />
                            <CardContent>
                                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                    <Link
                                        href={domain.defaultUrlUDM}
                                        id="udm-logo"
                                        sx={{
                                            ...appLinkStyles,
                                            p: 1,
                                        }}
                                    >
                                        <img
                                            src='/logo-udm.png'
                                            alt="Utility Device Manager logo"
                                            className="app-logo"
                                            data-cy-id="udm-logo"
                                            style={{ height: 'auto', maxHeight: '50px', maxWidth: '100%' }}
                                        />
                                    </Link>
                                    <Link
                                        href={domain.baseUrlEnerConnect}
                                        id="enerconnect-logo"
                                        sx={{
                                            ...appLinkStyles,
                                            p: 1,
                                            pt: 2
                                        }}
                                    >
                                        <img
                                            src='/logo-enerconnect.png'
                                            alt="EnerConnect logo"
                                            className="app-logo"
                                            data-cy-id="enerconnect-logo"
                                            style={{ height: 'auto', maxHeight: '40px', maxWidth: '100%' }}
                                        />
                                    </Link>
                                </Stack>
                                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                    <Link
                                        href={domain.defaultUrlSmartMAP}
                                        id="smartmap-logo"
                                        sx={appLinkStyles}
                                    >
                                        <img
                                            src='/logo-smartmap.png'
                                            alt="SmartMAP logo"
                                            className="app-logo"
                                            data-cy-id="smartmap-logo"
                                            style={{ height: 'auto', maxHeight: '22px', maxWidth: '100%' }}
                                        />
                                    </Link>
                                    <Link
                                        href={domain.defaultUrlHealthMAP}
                                        id="healthmap-logo"
                                        sx={appLinkStyles}
                                    >
                                        <img
                                            src='/logo-healthmap.png'
                                            alt="HealthMAP logo"
                                            className="app-logo"
                                            data-cy-id="healthmap-logo"
                                            style={{ height: 'auto', maxHeight: '22px', maxWidth: '100%' }}
                                        />
                                    </Link>
                                </Stack>
                                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                    <Link
                                        href={domain.defaultUrlSM}
                                        id="sm-logo"
                                        sx={appLinkStyles}
                                    >
                                        <img
                                            src='/logo-sm.png'
                                            alt="Settlement Manager logo"
                                            className="app-logo"
                                            data-cy-id="sm-logo"
                                            style={{ height: 'auto', maxHeight: '37px', maxWidth: '100%' }}
                                        />
                                    </Link>
                                </Stack>
                                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                    <Link
                                        href={domain.defaultUrlCIEM}
                                        id="ciem-logo"
                                        sx={appLinkStyles}
                                    >
                                        <img
                                            src='/logo-ciem.png'
                                            alt="C&I Energy Manager logo"
                                            className="app-logo"
                                            data-cy-id="ciem-logo"
                                            style={{ height: 'auto', maxHeight: '37px', maxWidth: '100%' }}
                                        />
                                    </Link>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>
                }
            </Box>
        </>
    );
}